import { useEffect, useRef, useState, type FormEvent } from "react";
import api from "@/api/axios";
import { ArrowRight, BadgeCheck, CalendarDays, ClipboardList, KeyRound, Link2, LockKeyhole, Power, Send, Settings2, Wallet, X } from "lucide-react";

type InfoPanelProps = {
  data: any;
  refresh: () => void;
  onNavigate: (tab: "session" | "config" | "records" | "membership") => void;
  sessionConnected: boolean;
  selectedCount: number;
  preview?: boolean;
};

export default function InfoPanel({ data, refresh, onNavigate, sessionConnected, selectedCount, preview = false }: InfoPanelProps) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [actionMessage, setActionMessage] = useState("");
  const [previewEnabled, setPreviewEnabled] = useState(!!data.checkin_enabled);
  const [isResetting, setIsResetting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const passwordButtonRef = useRef<HTMLButtonElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const resettingRef = useRef(isResetting);
  resettingRef.current = isResetting;
  const checkinEnabled = preview ? previewEnabled : !!data.checkin_enabled;

  useEffect(() => {
    if (!passwordOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    passwordInputRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !resettingRef.current) {
        setPasswordOpen(false);
        setNewPassword("");
        setConfirmPassword("");
        setPasswordError("");
        setPasswordSuccess("");
        requestAnimationFrame(() => passwordButtonRef.current?.focus());
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>("button:not([disabled]), input:not([disabled])") ?? []);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [passwordOpen]);

  const closePassword = () => {
    if (isResetting) return;
    setPasswordOpen(false);
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError("");
    setPasswordSuccess("");
    requestAnimationFrame(() => passwordButtonRef.current?.focus());
  };

  const handleResetPassword = async (event: FormEvent) => {
    event.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");
    if (!newPassword || !confirmPassword) return setPasswordError("请填写完整信息。");
    if (newPassword !== confirmPassword) return setPasswordError("两次输入的密码不一致。");
    if (newPassword.length < 6) return setPasswordError("密码长度不能少于 6 位。");
    if (preview) return setPasswordError("这是界面预览，不会修改密码。");
    try {
      setIsResetting(true);
      const response = await api.post("/api/reset_password", { new_password: newPassword });
      if (response.data.success) {
        setPasswordSuccess("密码已修改。");
        setNewPassword("");
        setConfirmPassword("");
      } else setPasswordError(response.data.message || "密码修改失败。");
    } catch {
      setPasswordError("密码修改失败，请稍后重试。");
    } finally {
      setIsResetting(false);
    }
  };

  const updateCheckin = async (enabled: boolean) => {
    if (preview) {
      setPreviewEnabled(enabled);
      setActionMessage("界面预览：状态不会保存。");
      return;
    }
    try {
      setIsUpdating(true);
      setActionMessage("");
      await api.post("/api/update_checkin_status", { enabled });
      refresh();
    } catch {
      setActionMessage("更新失败，请稍后重试。");
    } finally {
      setIsUpdating(false);
    }
  };

  const checkinNow = async () => {
    if (preview) {
      setActionMessage("界面预览：不会执行打卡。");
      return;
    }
    try {
      setIsCheckingIn(true);
      setActionMessage("");
      await api.post("/api/reset_next_checkin_time");
      setActionMessage("已清除下一次时间，稍后将立即打卡。");
    } catch {
      setActionMessage("触发失败，请稍后再试。");
    } finally {
      setIsCheckingIn(false);
    }
  };

  return (
    <div className="user-overview">
      <section className="user-card user-overview-panel" aria-labelledby="user-overview-title">
        <div className="user-overview-main">
          <div className="user-overview-topline">
            <div className="user-overview-status-icon"><Power size={22} /></div>
            <label className="user-overview-toggle"><span>{checkinEnabled ? "已开启" : "已暂停"}</span><span className="user-switch"><input type="checkbox" checked={checkinEnabled} disabled={isUpdating} onChange={(event) => updateCheckin(event.target.checked)} aria-label="切换自动打卡" /><span /></span></label>
          </div>
          <h2 id="user-overview-title">自动打卡</h2>
          <div className="user-overview-state"><span className={`user-status-badge ${checkinEnabled ? "is-on" : ""}`}><i />{checkinEnabled ? "运行中" : "已暂停"}</span><span>{sessionConnected ? "Telegram 已连接" : "Telegram 未连接"}</span><span>{sessionConnected ? `${selectedCount} 个打卡群组` : "暂无群组信息"}</span></div>
          <div className="user-overview-primary-actions"><button className="user-button user-button-primary" onClick={checkinNow} disabled={!checkinEnabled || isCheckingIn}><Send size={16} />{isCheckingIn ? "触发中..." : "立即打卡"}</button><button className="user-button user-button-secondary" onClick={() => onNavigate("config")}><Settings2 size={16} />打卡配置</button></div>
          {actionMessage && <p className="user-overview-message" role="status">{actionMessage}</p>}
        </div>
        <div className="user-overview-shortcuts" aria-label="功能入口">
          <button type="button" onClick={() => onNavigate("session")}><span className="user-shortcut-icon is-blue"><Link2 size={18} /></span><span>帐号连接</span><ArrowRight size={16} /></button>
          <button type="button" onClick={() => onNavigate("records")}><span className="user-shortcut-icon is-purple"><ClipboardList size={18} /></span><span>打卡记录</span><ArrowRight size={16} /></button>
          <button type="button" onClick={() => onNavigate("membership")}><span className="user-shortcut-icon is-amber"><Wallet size={18} /></span><span>会员中心</span><ArrowRight size={16} /></button>
          <button type="button" ref={passwordButtonRef} onClick={() => setPasswordOpen(true)}><span className="user-shortcut-icon is-green"><LockKeyhole size={18} /></span><span>修改密码</span><ArrowRight size={16} /></button>
        </div>
      </section>

      <section className="user-card user-overview-details" aria-label="账户信息">
        <div className="user-overview-detail"><span><Link2 size={16} />Telegram 账号</span><strong>{data.telegram_id || "—"}</strong></div>
        <div className="user-overview-detail"><span><BadgeCheck size={16} />用户昵称</span><strong>{data.user_nickname || "—"}</strong></div>
        <div className="user-overview-detail"><span><CalendarDays size={16} />会员到期</span><strong>{data.membership_expiry ? new Date(data.membership_expiry).toLocaleString("zh-CN") : "未开通会员"}</strong></div>
        <div className="user-overview-detail"><span><Wallet size={16} />账户余额</span><strong>¥{Number(data.balance || 0).toFixed(2)}</strong></div>
      </section>

      {passwordOpen && <div className="user-dialog-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) closePassword(); }}>
        <div className="user-password-dialog user-card" ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="user-password-title" aria-describedby="user-password-description">
          <div className="user-password-dialog-heading"><span className="user-password-dialog-icon"><KeyRound size={21} /></span><button type="button" className="user-password-close" onClick={closePassword} disabled={isResetting} aria-label="关闭修改密码窗口"><X size={19} /></button></div>
          <h2 id="user-password-title">修改密码</h2>
          <p id="user-password-description">新密码至少 6 位。</p>
          <form onSubmit={handleResetPassword} className="user-password-form">
            <label>新密码<input ref={passwordInputRef} type="password" autoComplete="new-password" value={newPassword} onChange={(event) => { setNewPassword(event.target.value); setPasswordError(""); }} placeholder="输入新密码" /></label>
            <label>确认密码<input type="password" autoComplete="new-password" value={confirmPassword} onChange={(event) => { setConfirmPassword(event.target.value); setPasswordError(""); }} placeholder="再次输入新密码" /></label>
            {passwordError && <p className="user-form-error" role="alert">{passwordError}</p>}
            {passwordSuccess && <p className="user-form-success" role="status">{passwordSuccess}</p>}
            <div className="user-password-dialog-actions"><button type="button" className="user-button user-button-secondary" onClick={closePassword} disabled={isResetting}>取消</button><button className="user-button user-button-primary" disabled={isResetting} type="submit">{isResetting ? "修改中..." : "更新密码"}</button></div>
          </form>
        </div>
      </div>}
    </div>
  );
}
